import React, { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Radar, RefreshCw, ExternalLink, Download, PhoneCall, StickyNote, X, Plus, Trash2, Power, ChevronDown, ChevronUp, ShieldCheck, Users, Clock } from "lucide-react";

const PORTAL_OPTS = ["", "subito", "idealista", "immobiliare", "casa.it", "casadaprivato", "bakeca", "trovacasa"];
const TYPE_OPTS = ["", "appartamento", "casa", "villa", "attico", "rustico", "loft", "terreno", "ufficio"];
const inputCls = "bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none placeholder:text-ink-500";

const timeOnline = (iso) => {
    const h = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3600000));
    if (h < 1) return "meno di 1h";
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}g`;
};
const fmtDT = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const compatDot = (s) => (s >= 85 ? "bg-green-400" : s >= 60 ? "bg-amber-400" : "bg-ink-400");

export default function NuoviAnnunciPrivati({ onNewCountChange }) {
    const [items, setItems] = useState(null);
    const [summary, setSummary] = useState(null);
    const [monitors, setMonitors] = useState([]);
    const [showMonitors, setShowMonitors] = useState(false);
    const [running, setRunning] = useState(false);
    const [flt, setFlt] = useState({ city: "", portal: "", property_type: "", min_price: "", max_price: "", min_sqm: "", published: "", only_new: false, only_matches: false, sort: "recent" });

    const load = async (f = flt) => {
        try {
            const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
            const [r, s] = await Promise.all([api.get("/crm/private-listings", { params }), api.get("/crm/private-listings/summary")]);
            setItems(r.data.items);
            setSummary(s.data);
        } catch { toast.error("Errore nel caricamento"); }
    };
    const loadMonitors = () => api.get("/crm/private-listings/monitors").then((r) => setMonitors(r.data.items || [])).catch(() => {});

    useEffect(() => {
        load();
        loadMonitors();
        const t = setTimeout(() => api.post("/crm/private-listings/mark-seen").then(() => onNewCountChange?.(0)).catch(() => {}), 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, []);

    const setF = (k, v) => { const f = { ...flt, [k]: v }; setFlt(f); load(f); };

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post("/crm/private-listings/monitors/run");
            toast.success(r.data.new_count > 0 ? `${r.data.new_count} nuovi annunci trovati` : "Nessun nuovo annuncio");
            await load(); loadMonitors();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento"); }
        finally { setRunning(false); }
    };

    if (items === null) return <div className="py-24 text-center text-ink-400 text-sm">Caricamento annunci privati...</div>;

    return (
        <div data-testid="nuovi-annunci-privati">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                <div>
                    <h2 className="font-serif text-3xl text-ink-50 flex items-center gap-3"><Radar size={22} className="text-gold" /> Nuovi Annunci Privati</h2>
                    <p className="text-sm text-ink-400 mt-2 max-w-2xl">Monitoraggio automatico dei portali (ogni 6 ore): nuovi annunci di privati nelle tue zone, con match automatico sui tuoi acquirenti. Archivio separato dal database immobili del CRM.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button data-testid="pl-run-now" onClick={runNow} disabled={running} className="cta-outline px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
                        <RefreshCw size={14} className={running ? "animate-spin" : ""} /> {running ? "Controllo..." : "Aggiorna ora"}
                    </button>
                    <button data-testid="pl-toggle-monitors" onClick={() => setShowMonitors((v) => !v)} className="cta-gold px-4 py-2 text-sm flex items-center gap-2">
                        <Radar size={14} /> Zone monitorate ({monitors.filter((m) => m.active).length}) {showMonitors ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                </div>
            </div>

            {summary?.today_count > 0 && (
                <div data-testid="pl-today-banner" className="mb-5 p-3 border border-gold/40 bg-gold/5 text-sm text-ink-50 flex items-center gap-2">
                    <ShieldCheck size={15} className="text-gold" /> Hai <b className="text-gold">{summary.today_count}</b> nuov{summary.today_count === 1 ? "o annuncio privato pubblicato" : "i annunci privati pubblicati"} nelle ultime 24 ore.
                </div>
            )}

            {showMonitors && <MonitorsPanel monitors={monitors} onChange={() => { loadMonitors(); load(); }} />}

            {monitors.length === 0 && !showMonitors && (
                <div data-testid="pl-empty-monitors" className="border border-dashed border-white/15 p-10 text-center mb-6">
                    <Radar size={26} className="mx-auto mb-3 text-ink-500" />
                    <p className="text-ink-400 text-sm mb-4">Non stai ancora monitorando nessuna zona. Aggiungi la tua prima zona per ricevere automaticamente i nuovi annunci di privati.</p>
                    <button data-testid="pl-add-first-monitor" onClick={() => setShowMonitors(true)} className="cta-gold px-5 py-2.5 text-sm">+ Aggiungi zona monitorata</button>
                </div>
            )}

            {/* Filtri */}
            <div className="flex flex-wrap items-center gap-2 mb-6 text-sm">
                <input data-testid="pl-flt-city" placeholder="Comune/zona" value={flt.city} onChange={(e) => setF("city", e.target.value)} className={`${inputCls} w-36`} />
                <select data-testid="pl-flt-portal" value={flt.portal} onChange={(e) => setF("portal", e.target.value)} className={`${inputCls} bg-ink-950`}>
                    {PORTAL_OPTS.map((p) => <option key={p} value={p}>{p === "" ? "Tutti i portali" : p}</option>)}
                </select>
                <select data-testid="pl-flt-type" value={flt.property_type} onChange={(e) => setF("property_type", e.target.value)} className={`${inputCls} bg-ink-950`}>
                    {TYPE_OPTS.map((t) => <option key={t} value={t}>{t === "" ? "Tipologia" : t}</option>)}
                </select>
                <input data-testid="pl-flt-minprice" type="number" placeholder="€ min" value={flt.min_price} onChange={(e) => setF("min_price", e.target.value)} className={`${inputCls} w-24`} />
                <input data-testid="pl-flt-maxprice" type="number" placeholder="€ max" value={flt.max_price} onChange={(e) => setF("max_price", e.target.value)} className={`${inputCls} w-24`} />
                <input data-testid="pl-flt-minsqm" type="number" placeholder="mq min" value={flt.min_sqm} onChange={(e) => setF("min_sqm", e.target.value)} className={`${inputCls} w-22`} />
                <select data-testid="pl-flt-published" value={flt.published} onChange={(e) => setF("published", e.target.value)} className={`${inputCls} bg-ink-950`}>
                    <option value="">Qualsiasi data</option><option value="today">Oggi</option><option value="week">Ultima settimana</option><option value="month">Ultimo mese</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs text-ink-100 cursor-pointer ml-1">
                    <input data-testid="pl-flt-onlynew" type="checkbox" checked={flt.only_new} onChange={(e) => setF("only_new", e.target.checked)} className="accent-[#c9a55c]" /> Solo nuovi
                </label>
                <label className="flex items-center gap-1.5 text-xs text-ink-100 cursor-pointer">
                    <input data-testid="pl-flt-onlymatches" type="checkbox" checked={flt.only_matches} onChange={(e) => setF("only_matches", e.target.checked)} className="accent-[#c9a55c]" /> Solo con clienti compatibili
                </label>
                <div className="ml-auto flex items-center gap-2 text-xs text-ink-400">
                    Ordina:
                    <select data-testid="pl-sort" value={flt.sort} onChange={(e) => setF("sort", e.target.value)} className={`${inputCls} bg-ink-950`}>
                        <option value="recent">Più recenti</option><option value="compat">Compatibilità più alta</option>
                        <option value="price_asc">Prezzo crescente</option><option value="price_desc">Prezzo decrescente</option>
                    </select>
                </div>
            </div>

            {items.length === 0 ? (
                <div data-testid="pl-empty" className="border border-white/10 p-12 text-center text-ink-400 text-sm">
                    Nessun annuncio in archivio con questi filtri. {monitors.length > 0 && "Il monitoraggio gira ogni 6 ore, oppure premi \"Aggiorna ora\"."}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((it) => <ListingCard key={it.id} it={it} onChanged={() => load()} />)}
                </div>
            )}
        </div>
    );
}

function MonitorsPanel({ monitors, onChange }) {
    const [nf, setNf] = useState({ city: "", zone: "", property_type: "", operation: "vendita", min_price: "", max_price: "" });
    const [saving, setSaving] = useState(false);

    const add = async () => {
        if (!nf.city.trim()) return toast.error("Indica la città da monitorare");
        setSaving(true);
        try {
            await api.post("/crm/private-listings/monitors", { filters: nf });
            toast.success("Zona monitorata aggiunta: primo controllo in corso...");
            setNf({ city: "", zone: "", property_type: "", operation: "vendita", min_price: "", max_price: "" });
            onChange();
            api.post("/crm/private-listings/monitors/run").then(() => onChange()).catch(() => {});
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    const toggle = (m) => api.put(`/crm/private-listings/monitors/${m.id}`, { active: !m.active }).then(onChange);
    const del = (m) => api.delete(`/crm/private-listings/monitors/${m.id}`).then(() => { toast.success("Monitoraggio eliminato"); onChange(); });

    return (
        <div data-testid="pl-monitors-panel" className="border border-white/10 bg-ink-900/40 p-5 mb-6">
            <div className="text-[10px] text-ink-400 uppercase tracking-wider mb-3">Aggiungi zona monitorata</div>
            <div className="flex flex-wrap gap-2 mb-4">
                <input data-testid="pl-mon-city" placeholder="Città *" value={nf.city} onChange={(e) => setNf({ ...nf, city: e.target.value })} className={`${inputCls} w-40`} />
                <input data-testid="pl-mon-zone" placeholder="Zona (opz.)" value={nf.zone} onChange={(e) => setNf({ ...nf, zone: e.target.value })} className={`${inputCls} w-36`} />
                <select data-testid="pl-mon-op" value={nf.operation} onChange={(e) => setNf({ ...nf, operation: e.target.value })} className={`${inputCls} bg-ink-950`}>
                    <option value="vendita">Vendita</option><option value="affitto">Affitto</option><option value="entrambi">Entrambi</option>
                </select>
                <select data-testid="pl-mon-type" value={nf.property_type} onChange={(e) => setNf({ ...nf, property_type: e.target.value })} className={`${inputCls} bg-ink-950`}>
                    {TYPE_OPTS.map((t) => <option key={t} value={t}>{t === "" ? "Tutte le tipologie" : t}</option>)}
                </select>
                <input data-testid="pl-mon-minprice" type="number" placeholder="€ min" value={nf.min_price} onChange={(e) => setNf({ ...nf, min_price: e.target.value })} className={`${inputCls} w-24`} />
                <input data-testid="pl-mon-maxprice" type="number" placeholder="€ max" value={nf.max_price} onChange={(e) => setNf({ ...nf, max_price: e.target.value })} className={`${inputCls} w-24`} />
                <button data-testid="pl-mon-add" onClick={add} disabled={saving} className="cta-gold px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"><Plus size={14} /> Aggiungi</button>
            </div>
            {monitors.length > 0 && (
                <div className="space-y-1.5">
                    {monitors.map((m) => (
                        <div key={m.id} data-testid={`pl-monitor-${m.id}`} className="flex items-center gap-3 text-sm py-1.5 px-3 border border-white/5 bg-ink-950/40">
                            <span className={`w-2 h-2 rounded-full ${m.active ? "bg-green-400" : "bg-ink-600"}`} />
                            <span className="text-ink-50">{m.name}</span>
                            <span className="text-xs text-ink-500">{m.filters?.operation || "vendita"}{m.filters?.max_price ? ` · fino a ${formatPrice(m.filters.max_price)}` : ""}</span>
                            <span className="ml-auto text-[11px] text-ink-500">{m.last_run_at ? `ultimo check ${fmtDT(m.last_run_at)} · ${m.last_new_count ?? 0} nuovi` : "mai eseguito"}</span>
                            <button data-testid={`pl-mon-toggle-${m.id}`} onClick={() => toggle(m)} title={m.active ? "Sospendi" : "Attiva"} className="text-ink-400 hover:text-gold"><Power size={13} /></button>
                            <button data-testid={`pl-mon-del-${m.id}`} onClick={() => del(m)} className="text-ink-400 hover:text-red-300"><Trash2 size={13} /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ListingCard({ it, onChanged }) {
    const [showMatches, setShowMatches] = useState(false);
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState(it.note || "");
    const [busy, setBusy] = useState(false);

    const doImport = async () => {
        if (!window.confirm("Importare in Pipeline (Acquisizione - Venditori)? Verrà creata solo la scheda proprietario: l'immobile ufficiale andrà creato dall'agenzia dopo aver preso l'incarico.")) return;
        setBusy(true);
        try {
            await api.post(`/crm/private-listings/${it.id}/import`, {});
            toast.success("Importato in Pipeline Venditori");
            onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore import"); }
        finally { setBusy(false); }
    };
    const planCall = async () => {
        try {
            const r = await api.post(`/crm/private-listings/${it.id}/plan-call`, {});
            toast.success(r.data.already_existed ? "Task già presente in Pipeline" : "Task 'Chiamare proprietario' creata");
        } catch { toast.error("Errore creazione task"); }
    };
    const dismiss = async () => {
        try { await api.post(`/crm/private-listings/${it.id}/dismiss`); toast.success("Annuncio ignorato"); onChanged(); }
        catch { toast.error("Errore"); }
    };
    const saveNote = async () => {
        try { await api.put(`/crm/private-listings/${it.id}/note`, { text: note }); toast.success("Nota salvata"); setNoteOpen(false); }
        catch { toast.error("Errore"); }
    };

    // Fallback intelligente: se l'immagine dell'immobile non è disponibile, uso il favicon/logo del portale.
    const portalLogo = useMemo(() => {
        if (!it.url) return null;
        try {
            const host = new URL(it.url).hostname;
            return `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
        } catch { return null; }
    }, [it.url]);

    const details = useMemo(() => [
        it.sqm ? `${it.sqm} mq` : null,
        it.rooms ? `${it.rooms} locali` : null,
        it.bathrooms ? `${it.bathrooms} bagni` : null,
        it.property_type, it.operation,
    ].filter(Boolean), [it]);

    return (
        <div data-testid={`pl-card-${it.id}`} className={`border bg-ink-900/40 flex flex-col ${it.status === "new" ? "border-gold/40" : it.status === "imported" ? "border-green-700/40" : "border-white/10"}`}>
            <a
                data-testid={`pl-hero-${it.id}`}
                href={it.url}
                target="_blank"
                rel="noreferrer"
                title="Apri annuncio nella fonte originale"
                className="relative block aspect-video bg-ink-950 overflow-hidden group"
            >
                {it.image ? (
                    <img
                        src={it.image}
                        alt={it.title || "annuncio"}
                        loading="lazy"
                        onError={(e) => {
                            // Se il caricamento dell'og:image fallisce → fallback al logo del portale
                            if (portalLogo && e.currentTarget.src !== portalLogo) {
                                e.currentTarget.src = portalLogo;
                                e.currentTarget.classList.add("pl-hero-logo-fit");
                            } else {
                                e.currentTarget.style.display = "none";
                                e.currentTarget.parentElement.classList.add("pl-hero-fallback");
                            }
                        }}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    />
                ) : portalLogo ? (
                    <div className="w-full h-full pl-hero-fallback flex items-center justify-center" aria-hidden="true">
                        <img src={portalLogo} alt="" loading="lazy" className="w-16 h-16 object-contain opacity-90" />
                        <div className="absolute bottom-2 left-0 right-0 text-center text-[10px] uppercase tracking-widest text-ink-500">
                            {it.source}
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full pl-hero-fallback flex items-center justify-center" aria-hidden="true">
                        <div className="text-center">
                            <div className="text-3xl mb-1 opacity-60">🏠</div>
                            <div className="text-[10px] uppercase tracking-widest text-ink-500">Anteprima non disponibile</div>
                        </div>
                    </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
                    <span data-testid="pl-badge-private" className="text-[10px] uppercase tracking-wider text-green-300 border border-green-700/50 bg-ink-950/85 backdrop-blur px-2 py-0.5">Privato {it.private_score}%</span>
                    {it.status === "new" && <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/50 bg-ink-950/85 backdrop-blur px-2 py-0.5">Nuovo</span>}
                    {it.status === "imported" && <span className="text-[10px] uppercase tracking-wider text-green-300 border border-green-700/50 bg-ink-950/85 backdrop-blur px-2 py-0.5">✓ Importato</span>}
                </div>
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider text-purple-200 border border-purple-700/50 bg-ink-950/85 backdrop-blur px-2 py-0.5">{it.source}</span>
            </a>
            <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-baseline justify-between gap-2">
                    <span data-testid={`pl-price-${it.id}`} className={`font-serif text-xl ${it.price ? "text-price" : "text-ink-500"}`}>
                        {it.price ? formatPrice(it.price) : "Prezzo n.d."}
                    </span>
                    <span className="text-[11px] text-ink-500 flex items-center gap-1"><Clock size={11} /> online da {timeOnline(it.first_seen_at)}</span>
                </div>
                <div data-testid={`pl-title-${it.id}`} className="text-ink-50 text-sm leading-snug mt-1 line-clamp-2 font-medium">
                    {it.title || "Titolo non disponibile"}
                </div>
                <div className="text-xs text-ink-400 mt-1">{[it.city, it.zone].filter(Boolean).join(" · ") || "—"}</div>
                {details.length > 0 && <div data-testid={`pl-details-${it.id}`} className="text-xs text-ink-300 mt-1.5">{details.join(" · ")}</div>}
                <div className="text-[11px] text-ink-500 mt-1">Rilevato il {fmtDT(it.first_seen_at)}</div>

                {/* Clienti compatibili */}
                <div className="mt-3">
                    {it.match_count > 0 ? (
                        <button data-testid={`pl-matches-toggle-${it.id}`} onClick={() => setShowMatches((v) => !v)}
                            className="w-full flex items-center gap-2 text-xs px-3 py-2 border border-green-700/40 bg-green-900/15 text-green-100 hover:bg-green-900/25">
                            <span className={`w-2 h-2 rounded-full ${compatDot(it.best_score)}`} />
                            <Users size={12} /> {it.match_count} client{it.match_count === 1 ? "e compatibile" : "i compatibili"} · max {it.best_score}%
                            {showMatches ? <ChevronUp size={12} className="ml-auto" /> : <ChevronDown size={12} className="ml-auto" />}
                        </button>
                    ) : (
                        <div className="text-[11px] text-ink-500 px-3 py-2 border border-white/5">Nessun cliente compatibile nel CRM</div>
                    )}
                    {showMatches && (
                        <div data-testid={`pl-matches-list-${it.id}`} className="border border-green-700/20 border-t-0 divide-y divide-white/5">
                            {(it.matches || []).map((m) => (
                                <div key={m.lead_id} className="p-3 text-xs">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${compatDot(m.score)}`} />
                                        <span className="text-ink-50 font-medium">{m.name}</span>
                                        <span className="ml-auto text-green-300 font-medium">{m.score}%</span>
                                    </div>
                                    <div className="text-ink-400 mt-1">
                                        {m.phone && <span>{m.phone} · </span>}
                                        {m.budget_max && <span>budget {formatPrice(m.budget_max)} · </span>}
                                        {m.cities?.length > 0 && <span>{m.cities.join(", ")} · </span>}
                                        richiesta del {new Date(m.requested_at).toLocaleDateString("it-IT")}
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                        {m.reasons.map((r) => <span key={r} className="text-[10px] text-green-200 bg-green-900/20 border border-green-700/30 px-1.5 py-0.5">✓ {r}</span>)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {noteOpen && (
                    <div className="mt-3">
                        <textarea data-testid={`pl-note-input-${it.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Nota sull'annuncio..." className={`${inputCls} w-full text-xs`} />
                        <div className="flex gap-2 mt-1">
                            <button data-testid={`pl-note-save-${it.id}`} onClick={saveNote} className="text-xs text-gold hover:underline">Salva nota</button>
                            <button onClick={() => setNoteOpen(false)} className="text-xs text-ink-400 hover:underline">Annulla</button>
                        </div>
                    </div>
                )}
                {!noteOpen && it.note && <div data-testid={`pl-note-text-${it.id}`} className="vd-note-box mt-2 text-[11px] px-2 py-1">📝 {it.note}</div>}

                {/* Azioni */}
                <div className="mt-auto pt-3 flex items-center gap-1 border-t border-white/5 mt-3">
                    <a data-testid={`pl-open-${it.id}`} href={it.url} target="_blank" rel="noreferrer" title="Apri annuncio / contatta proprietario"
                        className="flex-1 text-center text-xs py-2 border border-white/15 text-ink-100 hover:border-gold hover:text-gold flex items-center justify-center gap-1.5">
                        <ExternalLink size={12} /> Apri
                    </a>
                    {it.status !== "imported" && (
                        <button data-testid={`pl-import-${it.id}`} onClick={doImport} disabled={busy} title="Importa proprietario e immobile nel CRM"
                            className="flex-1 text-xs py-2 cta-gold flex items-center justify-center gap-1.5 disabled:opacity-60">
                            <Download size={12} /> Importa
                        </button>
                    )}
                    <button data-testid={`pl-call-${it.id}`} onClick={planCall} title="Pianifica chiamata" className="px-2.5 py-2 border border-white/15 text-ink-300 hover:text-gold hover:border-gold"><PhoneCall size={13} /></button>
                    <button data-testid={`pl-note-${it.id}`} onClick={() => setNoteOpen((v) => !v)} title="Note" className="px-2.5 py-2 border border-white/15 text-ink-300 hover:text-gold hover:border-gold"><StickyNote size={13} /></button>
                    <button data-testid={`pl-dismiss-${it.id}`} onClick={dismiss} title="Ignora annuncio" className="px-2.5 py-2 border border-white/15 text-ink-300 hover:text-red-300 hover:border-red-700/50"><X size={13} /></button>
                </div>
            </div>
        </div>
    );
}
